import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  CANONICAL_BASE_URL,
  OG_IMAGE,
  JSONLD_BLOCKS,
  getMetaForPath,
  DEFAULT_META,
} from "../lib/seoMeta";

/**
 * SEOHeadV2 — Iter 18 production SEO injector.
 * - Sets <title>, <meta name="description">
 * - Injects canonical link tag (unique per route)
 * - Injects Open Graph + Twitter Card metadata
 * - Injects 4 JSON-LD blocks (Organization, SoftwareApplication, SportsOrganization,
 *   MedicalOrganization) into a single <script type="application/ld+json"> tag
 *
 * Per-route metadata comes from /lib/seoMeta.js. Title/description can be
 * overridden via props for non-primary routes.
 */
export const SEOHeadV2 = ({ title, description, image = OG_IMAGE, schemaExtra = null }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const routeMeta = getMetaForPath(pathname);
    const finalTitle = title || routeMeta.title || DEFAULT_META.title;
    const finalDesc = description || routeMeta.description || DEFAULT_META.description;
    const canonicalUrl = `${CANONICAL_BASE_URL}${pathname === "/" ? "" : pathname}`;

    document.title = finalTitle;

    const upsertMeta = (selector, attrName, attrValue, contentValue) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", contentValue);
    };

    upsertMeta('meta[name="description"]', "name", "description", finalDesc);
    upsertMeta('meta[name="theme-color"]', "name", "theme-color", "#0d1f3c");

    // Open Graph (spec §12)
    upsertMeta('meta[property="og:site_name"]', "property", "og:site_name", "AuditIQ Sports");
    upsertMeta('meta[property="og:type"]', "property", "og:type", "website");
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    upsertMeta('meta[property="og:title"]', "property", "og:title", finalTitle);
    upsertMeta('meta[property="og:description"]', "property", "og:description", finalDesc);
    upsertMeta('meta[property="og:image"]', "property", "og:image", image);
    upsertMeta('meta[property="og:image:width"]', "property", "og:image:width", "1200");
    upsertMeta('meta[property="og:image:height"]', "property", "og:image:height", "630");

    // Twitter Card (spec §13)
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:site"]', "name", "twitter:site", "@AuditIQ");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", finalTitle);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", finalDesc);
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", image);

    // Canonical link tag — unique per route (spec §10)
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // JSON-LD — single script tag with all 4 blocks (spec §14)
    let ld = document.getElementById("auditiq-jsonld-v2");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "auditiq-jsonld-v2";
      document.head.appendChild(ld);
    }
    const blocks = schemaExtra
      ? [...JSONLD_BLOCKS, schemaExtra]
      : JSONLD_BLOCKS;
    ld.textContent = JSON.stringify(blocks);
  }, [pathname, title, description, image, schemaExtra]);

  return null;
};

export default SEOHeadV2;
